import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Container, Tooltip } from "react-bootstrap";
import { Info, Delete } from "@mui/icons-material";
import { useNotification } from "../../hooks/NotificationContext";
import { addField, updateField, deleteField, getFieldConfigurations } from "../../modules/config";
import AddForm from "./AddForm";
import EditableTable from "../elements/EditableTable";
import CustomIconButton from "../elements/CustomIconButton";
import EntityDeleteConfirmation from "./EntityDeleteConfirmation";
import { Grid, Typography } from "@mui/material";
import { getConfigDetails, getTableHeader } from "./tableData";

const subHeaderTabs = [
    { key: "name", label: "Name", width: "200px" },
];

const CustomFieldsComponent = ({ isAdmin, selectedTab }) => {
    const { addNotification } = useNotification();
    const { projectId } = useParams();
    const { group } = getConfigDetails(selectedTab);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [entityToDelete, setEntityToDelete] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await getFieldConfigurations(projectId);
            setData(response?.data || []);
        } catch (error) {
            addNotification("danger", error.message);
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDeleteClick = (id) => {
        setEntityToDelete(id);
        setShowConfirmationModal(true);
    };

    const handleConfirmDelete = async () => {
        setLoadingDelete(true);
        try {
            await deleteField(entityToDelete);
            fetchData();
            setShowConfirmationModal(false);
        } catch (error) {
            addNotification("danger", error.message);
        } finally {
            setLoadingDelete(false);
        }
    };

    const handleAddNewEntity = async (entity, parentRow = null) => {
        try {
            const payload = {
                name: entity.name,
                description: entity.description || null,
                parentId: parentRow?.id || null,
                type: "PROJECT",
                fieldType: parentRow ? "SUBCATEGORY" : "CATEGORY",
                gainssProjectId: projectId,
            };

            await addField(payload);
            fetchData();
        } catch (error) {
            addNotification("danger", error.message);
        }
    };

    const handleUpdateEntity = async (id, updatedData, isSub = false) => {
        try {
            const payload = {
                name: updatedData.name,
                description: updatedData.description || null,
                projectId: projectId,
            };

            await updateField(id, payload);
            fetchData();
        } catch (error) {
            addNotification("danger", error.message);
        }
    };

    const handleUpdateSubEntity = handleUpdateEntity;

    const handleDeleteSubEntity = handleConfirmDelete;

    return (
        <Container fluid className="px-0">
            <Typography variant="h6" className="mb-3">Reason for Not Automating</Typography>
            {isAdmin && (
                <Grid item xs={12} sx={{ mb: 2 }}>
                    <AddForm
                        entityType={selectedTab}
                        handleSubmit={handleAddNewEntity}
                        entity="Category"
                    />
                </Grid>
            )}

            <Grid item xs={12}>
                <EditableTable
                    data={data}
                    type={selectedTab}
                    headerTabs={getTableHeader(selectedTab)}
                    hasSubRows={true}
                    subHeaderTabs={subHeaderTabs}
                    subRowsKey="sub_categories"
                    subTableName="Subcategories"
                    subToggleTooltip="Show subcategories"
                    isAdmin={isAdmin}
                    isLoading={loading}
                    editableFields={["name"]}
                    handleUpdateEntity={handleUpdateEntity}
                    handleAddSubEntity={handleAddNewEntity}
                    handleUpdateSubEntity={handleUpdateSubEntity}
                    handleDeleteSubEntity={handleDeleteSubEntity}
                    action={(row) =>
                        row.gainss_project_id === null ? (
                            <Tooltip title="Can't be modified" arrow>
                                <Info sx={{ color: "info.main" }} />
                            </Tooltip>
                        ) : (
                            row.scope !== 'GLOBAL' &&
                            <CustomIconButton
                                title="Delete"
                                disableRipple
                                onClick={() => handleDeleteClick(row.id)}
                                disabled={row.is_default || loadingDelete}
                            >
                                <Delete sx={{ color: "red" }} />
                            </CustomIconButton>

                        )
                    }
                />

                <EntityDeleteConfirmation
                    show={showConfirmationModal}
                    handleClose={() => setShowConfirmationModal(false)}
                    handleConfirm={handleConfirmDelete}
                    entityToDelete={entityToDelete}
                    type={group}
                />
            </Grid>
        </Container>
    );
};

export default CustomFieldsComponent;
